import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { CASE_STATUS, caseStatusColor } from "@/lib/caseUtils";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LifeBuoy, CheckCircle2, RotateCcw } from "lucide-react";

export default function CaseDetailDialog({ open, onOpenChange, caseId, onChanged }) {
  const [c, setC] = useState(null);
  const [members, setMembers] = useState([]);
  const [note, setNote] = useState("");

  const load = () => api.get(`/cases/${caseId}`).then((r) => setC(r.data)).catch(() => { toast.error("Case not found"); onOpenChange(false); });
  useEffect(() => {
    if (!open || !caseId) return;
    load();
    api.get("/team/members").then((r) => setMembers(r.data)).catch(() => {});
  }, [open, caseId]);

  if (!open) return null;

  const patch = async (payload, msg) => {
    try { const { data } = await api.put(`/cases/${caseId}`, payload); setC(data); if (msg) toast.success(msg); onChanged && onChanged(); }
    catch (e) { toast.error("Update failed"); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    try { const { data } = await api.post(`/cases/${caseId}/notes`, { note }); setC(data); setNote(""); onChanged && onChanged(); }
    catch (e) { toast.error("Could not add note"); }
  };

  const isClosed = c && (c.status === "Closed" || c.status === "Resolved");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl rounded-sm max-h-[90vh] overflow-y-auto" data-testid="case-detail-dialog">
        {!c ? (<><DialogTitle className="sr-only">Case</DialogTitle><p className="text-muted-foreground py-6">Loading…</p></>) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 font-heading"><LifeBuoy size={18} className="text-primary" /> {c.title}</DialogTitle>
              <DialogDescription>{c.category} · opened {new Date(c.created_at).toLocaleString()} by {c.opened_by || "—"}{c.closed_at ? ` · closed ${new Date(c.closed_at).toLocaleString()}` : ""}</DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label>Status</Label>
                <Select value={c.status} onValueChange={(v) => patch({ status: v }, `Case ${v}`)}>
                  <SelectTrigger className={`rounded-sm ${caseStatusColor[c.status]}`} data-testid="case-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CASE_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5">
                <Label>Assigned to</Label>
                <Select value={c.tech_assigned || ""} onValueChange={(v) => patch({ tech_assigned: v }, "Case reassigned")}>
                  <SelectTrigger className="rounded-sm" data-testid="case-detail-tech"><SelectValue placeholder="Assign a team member" /></SelectTrigger>
                  <SelectContent>{members.map((m) => <SelectItem key={m.id} value={m.name}>{m.name}{m.role ? ` · ${m.role}` : ""}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            {c.description && (
              <div className="rounded-sm bg-secondary/60 px-3 py-2 text-sm">
                <p className="text-xs text-muted-foreground mb-1">Description</p>{c.description}
              </div>
            )}

            <div>
              <Label>Activity / resolution notes</Label>
              <div className="flex gap-2 mt-1.5">
                <Textarea data-testid="case-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="rounded-sm" placeholder="Add a work update…" />
                <Button className="rounded-sm" onClick={addNote} data-testid="case-add-note-btn">Add</Button>
              </div>
              <div className="space-y-2 mt-3" data-testid="case-notes-list">
                {(!c.notes || c.notes.length === 0) && <p className="text-sm text-muted-foreground">No activity yet.</p>}
                {[...(c.notes || [])].reverse().map((n) => (
                  <div key={n.id} className="rounded-sm border border-border p-3">
                    <p className="text-sm">{n.note}</p>
                    <p className="text-xs text-muted-foreground mt-1.5">{n.author || "—"} · {new Date(n.created_at).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              {isClosed ? (
                <Button variant="outline" className="rounded-sm gap-1.5" onClick={() => patch({ status: "Open" }, "Case reopened")} data-testid="case-reopen-btn"><RotateCcw size={15} /> Reopen</Button>
              ) : (
                <Button className="rounded-sm gap-1.5" onClick={() => patch({ status: "Closed" }, "Case closed")} data-testid="case-close-btn"><CheckCircle2 size={15} /> Close Case</Button>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
